import fox from "../../assets/reading-fox.png";
import { useState } from "react";

type Props = {
  userName: string;
  onComplete: () => void;
};

export default function FamilyGate({ userName, onComplete }: Props) {
  const [mode, setMode] = useState<"choose" | "create" | "join">("choose");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/api/family", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name || "Our Family" }),
      });
      if (!res.ok) throw new Error((await res.json()).error);
      onComplete();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed");
      setLoading(false);
    }
  };

  const handleJoin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/api/family/join", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ inviteCode: code.toUpperCase().trim() }),
      });
      if (!res.ok) throw new Error((await res.json()).error);
      onComplete();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed");
      setLoading(false);
    }
  };

  const cardStyle: React.CSSProperties = {
    display: "flex", flexDirection: "column", gap: "1rem",
    padding: "2rem", background: "var(--card)",
    borderRadius: "var(--radius)",
    boxShadow: "var(--card-shadow)",
    border: "1px solid var(--surface-border)",
    width: "min(400px, 100%)", boxSizing: "border-box",
  };

  const inputStyle: React.CSSProperties = {
    padding: "0.75rem", borderRadius: "var(--radius-sm)",
    border: "1px solid var(--border)",
    background: "var(--bg)", color: "var(--text)",
    fontSize: "1rem", fontFamily: "inherit",
  };

  const btnStyle: React.CSSProperties = {
    padding: "0.75rem", borderRadius: "var(--radius-sm)", border: "none",
    background: "var(--orange)", color: "var(--ink)",
    cursor: "pointer", fontSize: "1rem", fontFamily: "inherit",
    fontWeight: 500,
  };

  const linkStyle: React.CSSProperties = {
    background: "none", border: "none", color: "var(--blue-dark)",
    cursor: "pointer", fontSize: "0.9rem", fontFamily: "inherit",
    textDecoration: "underline", padding: 0,
  };

  return (
    <main style={{
      display: "flex", justifyContent: "center", alignItems: "center",
      minHeight: "100dvh", padding: "24px", boxSizing: "border-box", fontFamily: "var(--font-family)",
      background: "var(--bg)",
    }}>
      {mode === "choose" && (
        <div dir="rtl" style={{ ...cardStyle, alignItems: "center" }}>
          <img className="brand-fox" src={fox} alt="" />
          <h1 style={{ margin: 0, color: "var(--text)", fontSize: "1.5rem" }}>שלום {userName}!</h1>
          <p style={{ margin: 0, color: "var(--text-secondary)", fontSize: "0.9rem" }}>
            כדי להתחיל, צרו משפחה חדשה או הצטרפו לקיימת
          </p>
          <button onClick={() => setMode("create")} style={{ ...btnStyle, width: "100%" }}>
            יצירת משפחה חדשה
          </button>
          <button onClick={() => setMode("join")} style={{
            ...btnStyle, width: "100%",
            background: "transparent", color: "var(--blue-dark)",
            border: "1px solid var(--surface-border)",
          }}>
            הצטרפות עם קוד
          </button>
        </div>
      )}

      {mode === "create" && (
        <form onSubmit={handleCreate} dir="rtl" style={cardStyle} aria-labelledby="create-heading">
          <img className="brand-fox" src={fox} alt="" />
          <h1 id="create-heading" style={{ margin: 0, color: "var(--text)", fontSize: "1.5rem" }}>משפחה חדשה</h1>
          <label htmlFor="family-name" className="sr-only">שם המשפחה</label>
          <input
            id="family-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="שם המשפחה"
            autoFocus
            style={inputStyle}
          />
          {error && <p role="alert" style={{ color: "var(--wrong)", margin: 0 }}>{error}</p>}
          <button type="submit" disabled={loading} style={btnStyle}>
            {loading ? "..." : "יצירה"}
          </button>
          <button type="button" onClick={() => { setMode("choose"); setError(""); }} style={linkStyle}>
            חזרה
          </button>
        </form>
      )}

      {mode === "join" && (
        <form onSubmit={handleJoin} dir="rtl" style={cardStyle} aria-labelledby="join-heading">
          <img className="brand-fox" src={fox} alt="" />
          <h1 id="join-heading" style={{ margin: 0, color: "var(--text)", fontSize: "1.5rem" }}>הצטרפות למשפחה</h1>
          <label htmlFor="invite-code" className="sr-only">קוד הזמנה</label>
          <input
            id="invite-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="קוד הזמנה"
            autoFocus
            maxLength={16}
            style={{ ...inputStyle, textAlign: "center", letterSpacing: "0.2em", textTransform: "uppercase" }}
          />
          {error && <p role="alert" style={{ color: "var(--wrong)", margin: 0 }}>{error}</p>}
          <button type="submit" disabled={loading || code.trim().length < 4} style={btnStyle}>
            {loading ? "..." : "הצטרפות"}
          </button>
          <button type="button" onClick={() => { setMode("choose"); setError(""); }} style={linkStyle}>
            חזרה
          </button>
        </form>
      )}
    </main>
  );
}
